import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import SearchPanel from "@widgets/SearchPanel";
import { fnGetDataSet, fnSave } from "@shared/api/restApi";
import {
  CalAm,
  getInitFormData,
  CalAmBulk,
  getTablebySql,
  getNoSlip,
} from "@shared/utils/CommonUtils";
import { showDialog } from "@widgets/CommonDialog";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { menuInfo } from "@shared/type/CommonType";
import { showPopupModal } from "@widgets/CommonModal";
import SnTabHeader from "@shared/components/SnTabHeader";
import SnButton from "@shared/components/SnButton";
import SnTab from "@shared/components/SnTab";
import { global, useMenuStore } from "@shared/libs/Global";
import SN_MTL_INV_REG_001_TEX_POP_01 from "@menu/XTC/SN_MTL_INV_REG_001_TEX/SN_MTL_INV_REG_001_TEX_POP_01";
import SnResizeBar from "@shared/components/SnResizeBar";
import { dateToString } from "@shared/utils/DateUtils";
import { RowNode } from "ag-grid-community";
import { CellValueChangedEvent } from "@shared/components/GridV2/SnGridTypes";
import SnFileUploadBox from "@shared/components/SnFileUploadBox";

interface Props {
  Menuprops: menuInfo;
}

interface RowData {
  KeyField?: string;
  [key: string]: any;
}

interface SizeMapValue {
  label: string;
  sort: number;
}

const SN_MTL_INV_REG_001_TEX = forwardRef(({ Menuprops }: Props, ref) => {
  const { userInfo } = global();
  const { menuItems } = useMenuStore();
  const [selectedDocu, setSelectedDocu] = useState<string>("");
  const [selectedLineDocu, setSelectedLineDocu] = useState<number>(0);
  const searchRef = useRef<any>(null);
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid1: React.createRef(),
    grid2: React.createRef(),
    grid3: React.createRef(),
  });
  const focusKeyRef = useRef<{
    no_io?: string;
  } | null>(null);
  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
  });

  const [selectedTab, setSelectedTab] = useState("1");
  const [focusedRowData, setFocusedRowData] = useState<any>();
  const pivotLongDataRef = useRef<any[]>([]);
  // cd_var3 넣기위한
  const sizeToSlotRef = useRef<Map<string, string>>(new Map());
  // 고정 슬롯(qt_wo1~qt_wo20)을 유지하고 헤더/노출만 변경한다.
  const sizeSlotColumns = useMemo(
    () => Array.from({ length: 20 }, (_, index) => `qt_wo${index + 1}`),
    []
  );
  const sizeSortMapRef = useRef<Map<string, number>>(new Map());
  const SIZE_SORT_FALLBACK = 9999;
  const sizeCodeCollator = useMemo(
    () =>
      new Intl.Collator(undefined, {
        numeric: true,
        sensitivity: "base",
      }),
    []
  );

  const toFiniteNumber = (value: any, fallback = SIZE_SORT_FALLBACK) => {
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : fallback;
  };

  const getSizeSort = (sizeKey: string, row?: any) => {
    const mapSort = sizeSortMapRef.current.get(sizeKey);
    if (mapSort !== undefined) return mapSort;
    return toFiniteNumber(row?.no_seq_sort);
  };

  const addSizeToMap = (sizeMap: Map<string, SizeMapValue>, row: any) => {
    const sizeKey = `${row.cd_var3 ?? ""}`.trim();
    if (!sizeKey) return;

    const sizeLabel = `${row.nm_var3 ?? sizeKey}`.trim() || sizeKey;
    const sizeSort = getSizeSort(sizeKey, row);
    const current = sizeMap.get(sizeKey);
    if (!current || sizeSort < current.sort) {
      sizeMap.set(sizeKey, { label: sizeLabel, sort: sizeSort });
    }
  };

  const getSortedSizeList = (sizeMap: Map<string, SizeMapValue>) =>
    Array.from(sizeMap, ([key, value]) => ({
      key,
      label: value.label,
      sort: value.sort,
    })).sort((a, b) => {
      if (a.sort !== b.sort) return a.sort - b.sort;
      return sizeCodeCollator.compare(a.key, b.key);
    });

  const loadSizeSortMap = async () => {
    const rows = await getTablebySql({
      sql: `
        select cd_flag, no_seq_sort
          from snotes.mas_codel
         where cd_firm = :cd_firm
           and cd_clas = 'MAS168'
           and yn_use = 'Y'
         order by coalesce(no_seq_sort, 9999), cd_flag
      `,
      cd_firm: userInfo.cd_firm,
    });
    const nextMap = new Map<string, number>();
    (rows || []).forEach((row: any) => {
      const sizeKey = `${row.cd_flag ?? ""}`.trim();
      if (!sizeKey) return;
      nextMap.set(sizeKey, toFiniteNumber(row.no_seq_sort));
    });
    sizeSortMapRef.current = nextMap;
  };

  const ensureSizeSortMap = async () => {
    if (sizeSortMapRef.current.size > 0) return;
    await loadSizeSortMap();
  };

  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
  }));

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData); // 조회조건 항목 설정
        setFormData(rtn.formData); // 조회조건 초기값
        setColums({
          grid1: rtn.grid.grid1,
          grid2: rtn.grid.grid2,
          grid3: rtn.grid.grid3,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  const onSearch = async () => {
    const modifiedRowsH = gridRefs.current["grid1"].current.getChange();
    const modifiedRowsL = gridRefs.current["grid2"].current.getChange();
    if (
      modifiedRowsH.modifiedRowcount > 0 ||
      modifiedRowsL.modifiedRowcount > 0
    ) {
      const confirm = await showDialog(
        "confirm",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        const isSaved = await onSave();
        if (!isSaved) return;
      }
    }

    const selectedFormData = searchRef.current.getFormdata();
    if (selectedFormData.dt_from > selectedFormData.dt_to) {
      await showDialog(
        "alter",
        " 조회기간의 종료일이 시작일보다빠릅니다. 확인 후 다시 조회해주시기 바랍니다."
      );
      return;
    }

    try {
      const params = {
        id: "sn_mtl_inv_reg_001_tex_01",
        params: searchRef.current.getFormdata(),
      };
      setSelectedDocu("");
      setFocusedRowData(0);

      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current.resetUpdate();
      gridRefs.current["grid3"].current.resetUpdate();
      const data: any = await fnGetDataSet(params);
      setDataSources({
        grid1: data[0],
        grid2: data[1] || [],
        grid3: data[2] || [],
      });
      pivotLongDataRef.current = data[2] || [];
    } catch (error: any) {}
  };

  const onInsert = async () => {
    try {
      const salsohChanges =
        gridRefs.current["grid1"].current.getChange().insert;
      const ctrlValue = searchRef.current.getFormdata();
      if (salsohChanges.length > 0) return false;

      const today = dateToString(new Date());
      const newRow = {
        cd_firm: userInfo?.cd_firm,
        cd_biz: userInfo?.cd_biz,
        cd_biz_isu: userInfo?.cd_biz,
        no_po: "",
        cd_emp: userInfo?.cd_emp,
        nm_emp: userInfo?.nm_emp,
        cd_gp: userInfo?.cd_gp,
        cd_so: "1140",
        dt_req: today,
        dt_io: today,
        cd_exch: "000",
        rt_exch: 1,
        tp_um: "",
        fg_slip: "2",
        fg_io: "1",
        cd_cust: ctrlValue.cd_cust,
        nm_cust: ctrlValue.nm_cust,
        cd_dept: userInfo?.cd_dept || "1000",
        cd_cust_dlv: ctrlValue.cd_cust,
        am: 0,
        am_k: 0,
        cd_wh: "",
      };
      gridRefs.current["grid1"].current?.addRow(newRow);
    } catch (error: any) {}
  };

  const onAddRow = () => {
    try {
      gridRefs.current["grid1"].current?.stopEditing();
      const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
      if (focusedNodeH == null) {
        showDialog("alter", "선택된 행이 없습니다.");
        return false;
      }

      const requiredValuesCheckH =
        gridRefs.current["grid1"].current.requiredValuesCheck();
      if (!requiredValuesCheckH) {
        return false;
      }

      const selectedNoIo = focusedNodeH?.data.no_io;
      const maxNoLine =
        gridRefs.current["grid2"].current.getMaxValue("no_line_var");
      const today = dateToString(new Date());
      const newRow = {
        no_io: selectedNoIo,
        cd_firm: userInfo.cd_firm,
        cd_biz: userInfo?.cd_biz,
        cd_wh: focusedNodeH.data?.cd_wh,
        no_line_var: maxNoLine + 1,
        dt_io: today,
        dt_dlv: today,
        fg_slip: "2",
        fg_io: "1",
        cd_cust_origin: "",
        st_req: "F",
        cd_dummy01: "",
      };
      return gridRefs.current["grid2"].current?.addRow(newRow);
    } catch (error: any) {}
  };

  const onDelete = async () => {
    if (gridRefs.current["grid1"].current?.getDisplayedRowCount() < 1) {
      return;
    }

    const confirm = await showDialog("confirm", "삭제하시겠습니까?");
    if (!confirm) return;

    gridRefs.current["grid2"].current?.deleteAllRows();
    gridRefs.current["grid1"].current?.deleteSelectedRows();

    const realDeletedRows =
      gridRefs.current["grid1"].current.getChange().delete ?? [];
    if (Array.isArray(realDeletedRows) && realDeletedRows.length < 1) {
      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current.resetUpdate();
      return;
    }
    return await onSave();
  };

  const onSave = async () => {
    let mtlQtiohChanges;
    let mtlQtioChanges;
    let mtlQtioTexChanges;
    let salIsureqhChanges;
    let salIsureqlChanges;

    try {
      gridRefs.current["grid1"].current?.stopEditing();
      gridRefs.current["grid2"].current?.stopEditing();

      const requiredValuesCheckH =
        gridRefs.current["grid1"].current.requiredValuesCheck();
      if (!requiredValuesCheckH) return false;

      const requiredValuesCheckL =
        gridRefs.current["grid2"].current.requiredValuesCheck();
      if (!requiredValuesCheckL) return false;
      mtlQtiohChanges = gridRefs.current["grid1"].current.getChange();
      salIsureqhChanges = gridRefs.current["grid1"].current.getChange();
      mtlQtioChanges = gridRefs.current["grid2"].current.getChange();
      mtlQtioTexChanges = gridRefs.current["grid2"].current.getChange();
      salIsureqlChanges = gridRefs.current["grid2"].current.getChange();

      const sourceRows = [
        ...(mtlQtioChanges.insert || []),
        ...(mtlQtioChanges.update || []),
      ];

      const groupedMap = new Map();
      let noLineSeq = 1;

      for (const row of sourceRows) {
        const key = `${row.cd_dummy01}||${row.no_style}||${row.cd_item}||${row.cd_var1}||${row.cd_var2}`;

        // 🔥 그룹 없으면 새로 생성
        if (!groupedMap.has(key)) {
          const noLine = noLineSeq++;
          groupedMap.set(key, {
            ...row,
            qt: 0,
            am: 0,
            am_k: 0,
            no_line: noLine, // 🔥 무조건 그룹 기준으로 생성
            no_line_req: noLine,
            no_line_order_set: new Set(),
            _isUpdate: false,
          });
        }

        const group = groupedMap.get(key);

        // 🔥 SUM
        group.qt += Number(row.qt ?? 0);
        group.am += Number(row.am ?? 0);
        group.am_k += Number(row.am_k ?? 0);

        // 🔥 order 관리
        if (row.no_line_so) {
          group.no_line_order_set.add(row.no_line_so);
        }

        // 🔥 update 여부
        if ((mtlQtioChanges.update || []).includes(row)) {
          group._isUpdate = true;
        }
      }
      const groupedInsertList: any[] = [];
      const groupedUpdateList: any[] = [];

      for (const group of groupedMap.values()) {
        const orderList = Array.from(group.no_line_order_set);

        const result = {
          ...group,
          no_line_order: orderList[0] ?? null,
        };

        if (group._isUpdate) {
          groupedUpdateList.push(result);
        } else {
          groupedInsertList.push(result);
        }
      }
      for (let lotRow of mtlQtioTexChanges.insert || []) {
        const key = `${lotRow.cd_dummy01}||${lotRow.no_style}||${lotRow.cd_item}||${lotRow.cd_var1}||${lotRow.cd_var2}`;

        const group = groupedMap.get(key);

        if (group) {
          lotRow.no_line = group.no_line;
        }
      }
      const headerInserts = mtlQtiohChanges.insert ?? [];
      const lineInserts = mtlQtioTexChanges.insert ?? [];
      if (headerInserts.length > 0 && lineInserts.length === 0) {
        await showDialog("alter", "저장에 실패했습니다.");
        return false;
      }
      if (
        mtlQtiohChanges.modifiedRowcount === 0 &&
        mtlQtioTexChanges.modifiedRowcount === 0
      ) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      for (let row of headerInserts) {
        if (!row.no_io) {
          const cd_module: any = Menuprops.cd_module;
          const cd_menu: any = Menuprops.cd_menu;
          try {
            // getNoSlip 호출
            const no_io = await getNoSlip(
              cd_module,
              1, // no_line 값이 필요한 경우 사용
              "MPI", // cd_prefix 값이 필요하다면 여기에 적어주세요.
              row.dt_io, // 해당 날짜를 넘겨줘야 할 경우
              cd_menu // cd_user 값
            );
            if (no_io) {
              row.no_io = no_io;

              const focusedNodeH =
                gridRefs.current["grid1"].current.focusedRowNode();
              if (focusedNodeH) focusedNodeH.setDataValue("no_io", no_io);

              for (let row of salIsureqhChanges.insert) {
                if (!row.no_req) {
                  row.no_req = no_io;
                }
              }
              for (let row of lineInserts) {
                if (!row.no_io) {
                  row.no_io = no_io;
                }
              }
              for (let row of groupedInsertList) {
                if (!row.no_io) {
                  row.no_io = no_io;
                  row.no_req = no_io;
                }
              }
              gridRefs.current["grid2"].current.applyFilters({ no_io: no_io });
            }
          } catch (error) {}
        }
      }

      const saveData = {
        mtl_qtioh: mtlQtiohChanges,
        mtl_qtio: {
          insert: groupedInsertList,
          update: groupedUpdateList,
          delete: mtlQtioChanges.delete,
        },
        mtl_qtio_tex: mtlQtioTexChanges,
        sal_isureqh: salIsureqhChanges,
        sal_isureql: {
          insert: groupedInsertList,
          update: groupedUpdateList,
          delete: salIsureqlChanges.delete,
        },
      };
      const ok = await fnSave("SN_MTL_INV_REG_001_TEX_SAVE_01", saveData);
      if (!ok) return false;

      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current.resetUpdate();
      gridRefs.current["grid3"].current.resetUpdate();

      const focusedRow = gridRefs.current["grid1"].current.focusedRowNode();

      if (focusedRow) {
        focusKeyRef.current = {
          no_io: focusedRow.data.no_io,
        };
      }
      await onSearch();

      return true;
    } catch (error: any) {
      gridRefs.current["grid1"].current.resetUpdate(true);
      gridRefs.current["grid2"].current.resetUpdate(true);
      gridRefs.current["grid3"].current.resetUpdate(true);
      return false;
    }
  };

  const onBtnsClick = async (id: string) => {
    if (id === "aButtonApply") {
      const purPohChanges =
        gridRefs.current["grid1"].current.getChange().insert;
      if (purPohChanges.length === 0) {
        await showDialog("alter", "신규행을 추가하시기 바랍니다.");
        return;
      }
      const focusedRowNodeH =
        gridRefs.current["grid1"].current.focusedRowNode();
      if (
        focusedRowNodeH.data.cd_wh === "" ||
        focusedRowNodeH.data.cd_wh === null
      ) {
        await showDialog("alter", "창고를 먼저 선택해주세요.");
        return;
      }
      const selectedFormData = searchRef.current.getFormdata();
      const selectedData = {
        cd_biz: selectedFormData.cd_biz,
        cd_cust: selectedFormData.cd_cust,
        dt_from: selectedFormData.dt_from,
        dt_to: selectedFormData.dt_to,
        cd_wh: focusedRowNodeH.data.cd_wh,
        nm_wh: focusedRowNodeH.data.nm_wh,
      };

      const component = (
        <SN_MTL_INV_REG_001_TEX_POP_01 selectedData={selectedData} />
      );
      const result = await showPopupModal({
        title: "수주적용",
        children: component,
        validation: false,
        width: "69%",
      });
      if (!result || result === "close") return;

      const headerResult = result[0];
      const lineResult = result[1];
      const lineResult2 = result[2];
      const lineResult3 = result[3];
      const lineResult4 = result[4];
      if (focusedRowNodeH && headerResult[0]) {
        for (const key in headerResult[0]) {
          focusedRowNodeH.setDataValue(key, headerResult[0][key], "noEvent");
        }
      }

      lineResult.map((result: any) => {
        if (headerResult[0]) {
          const addedNode = onAddRow();
          if (addedNode) {
            for (const key in result) {
              addedNode.setDataValue(key, result[key], "noEvent");
            }
            addedNode.setDataValue("qt", result.stock_qt, "noEvent");
            addedNode.setDataValue("qt_w", result.stock_qt, "noEvent");
            addedNode.setDataValue("no_po", headerResult[0].no_po, "noEvent");
            addedNode.setDataValue("no_order", result.no_so, "noEvent");
            addedNode.setDataValue("no_so", result.no_so, "noEvent");
            addedNode.setDataValue("no_line_so", result.no_line, "noEvent");
            addedNode.setDataValue(
              "no_line_order",
              result.no_line_tex,
              "noEvent"
            );
          }
        }
      });

      lineResult2.map((result: any) => {
        if (headerResult[0]) {
          const addedNode = onAddRow();
          if (addedNode) {
            for (const key in result) {
              addedNode.setDataValue(key, result[key], "noEvent");
            }
            addedNode.setDataValue("qt", result.stock_qt, "noEvent");
            addedNode.setDataValue("qt_w", result.stock_qt, "noEvent");
            addedNode.setDataValue("no_order", result.no_so, "noEvent");
            addedNode.setDataValue("no_so", result.no_so, "noEvent");
            addedNode.setDataValue("no_line_so", result.no_line, "noEvent");
            addedNode.setDataValue(
              "no_line_order",
              result.no_line_tex,
              "noEvent"
            );
          }
        }
      });
      lineResult3.map((result: any) => {
        if (headerResult[0]) {
          const addedNode = onAddRow();
          if (addedNode) {
            for (const key in result) {
              addedNode.setDataValue(key, result[key], "noEvent");
            }
            addedNode.setDataValue("qt", result.stock_qt, "noEvent");
            addedNode.setDataValue("qt_w", result.stock_qt, "noEvent");
            addedNode.setDataValue(
              "qt_divide",
              result.stock_qt_divide,
              "noEvent"
            );
            addedNode.setDataValue(
              "qt_divide_w",
              result.stock_qt_divide,
              "noEvent"
            );
            addedNode.setDataValue("no_order", result.no_so, "noEvent");
            addedNode.setDataValue("no_so", result.no_so, "noEvent");
            addedNode.setDataValue("no_line_so", result.no_line, "noEvent");
            addedNode.setDataValue(
              "no_line_order",
              result.no_line_tex,
              "noEvent"
            );
          }
        }
      });

      lineResult4.map((result: any) => {
        if (headerResult[0]) {
          const addedNode = onAddRow();
          if (addedNode) {
            for (const key in result) {
              addedNode.setDataValue(key, result[key], "noEvent");
            }
            addedNode.setDataValue("qt", result.stock_qt, "noEvent");
            addedNode.setDataValue("qt_w", result.stock_qt, "noEvent");
            addedNode.setDataValue("no_po", headerResult[0].no_po, "noEvent");
            addedNode.setDataValue("no_order", result.no_so, "noEvent");
            addedNode.setDataValue("no_so", result.no_so, "noEvent");
            addedNode.setDataValue("no_line_so", result.no_line, "noEvent");
            addedNode.setDataValue(
              "no_line_order",
              result.no_line_tex,
              "noEvent"
            );
          }
        }
      });

      if (
        (lineResult2.length > 0 ||
          lineResult3.length > 0 ||
          lineResult4.length > 0 ||
          lineResult.length > 0) &&
        headerResult[0]
      ) {
        await CalAmBulk(
          gridRefs.current["grid1"],
          gridRefs.current["grid2"],
          "rt_exch"
        );
        gridRefs.current["grid2"].current.calculateTotals();
        setFooterDataToRow();
      }
    }
  };

  const onFocusedRowChanged = async (event: any) => {
    const selectedRow = event;
    const virtual: any[] = [];
    // gridRefs.current["grid2"].current.changeColumnConfigs(visible);
    await ensureSizeSortMap();

    // 선택된 수주(no_so)만 뽑아서 피벗 대상 데이터를 최소화한다.
    const filteredLongRows = pivotLongDataRef.current.filter(
      (row: any) => row.no_io === selectedRow.no_io
    );
    // 사이즈 목록 생성(cd_var3 -> 헤더 라벨).
    const sizeMap = new Map<string, SizeMapValue>();
    filteredLongRows.forEach((row: any) => {
      addSizeToMap(sizeMap, row);
    });
    const sizeList = getSortedSizeList(sizeMap);
    // 사이즈를 고정 슬롯(qt_wo1~qt_wo20)에 순서대로 매핑.
    const sizeToSlot = new Map<string, string>();
    sizeList.slice(0, sizeSlotColumns.length).forEach((size, index) => {
      sizeToSlot.set(size.key, sizeSlotColumns[index]);
    });
    const slotLabelByColumn = new Map<string, string>();
    sizeList.slice(0, sizeSlotColumns.length).forEach((size, index) => {
      slotLabelByColumn.set(sizeSlotColumns[index], size.label);
    });
    // (style, fit, color, location) 기준으로 피벗 row 구성 및 합산.
    const groupedRows = new Map<string, any>();
    filteredLongRows.forEach((row: any) => {
      const rowKey = [
        row.no_style ?? "",
        row.cd_var1 ?? "",
        row.cd_var2 ?? "",
        row.cd_dummy01 ?? "",
      ].join("|");
      let pivotRow = groupedRows.get(rowKey);
      if (!pivotRow) {
        pivotRow = {
          no_style: row.no_style,
          nm_style: row.nm_style,
          no_io: row.no_io,
          no_po: row.no_po,
          cd_var1: row.cd_var1,
          cd_var2: row.cd_var2,
          cd_dummy01: row.cd_dummy01,
          qt: 0,
        };
        sizeSlotColumns.forEach((slot) => {
          pivotRow[slot] = 0;
        });
        groupedRows.set(rowKey, pivotRow);
      }
      const slot = sizeToSlot.get(`${row.cd_var3 ?? ""}`.trim());
      if (slot) {
        pivotRow[slot] = Number(pivotRow[slot] ?? 0) + Number(row.qt ?? 0);
        pivotRow.qt += Number(row.qt ?? 0);
      }
    });
    // 4) 피벗 rowData 갱신: 탭 전환 없이도 즉시 반영되도록 상태를 교체한다.
    setDataSources((prev: any) => ({
      ...prev,
      grid3: Array.from(groupedRows.values()),
    }));
    if (selectedRow) {
      const no_io = selectedRow.no_io;

      gridRefs.current["grid3"].current.applyFilters({ no_io: no_io });
      gridRefs.current["grid3"].current.selectAndFocusRow(0);
      gridRefs.current["grid2"].current.applyFilters({ no_io: no_io });
      gridRefs.current["grid2"].current.selectAndFocusRow(0);
      setSelectedDocu(no_io);

      columns.grid3.map((row: any) => {
        if (row.cd_column && sizeSlotColumns.includes(row.cd_column)) {
          const slotLabel = slotLabelByColumn.get(row.cd_column);
          const data = {
            cd_column: row.cd_column,
            nm_column: slotLabel ?? row.nm_column,
            fg_visible: slotLabel ? "Y" : "N",
          };
          virtual.push(data);
        }
      });
      setTimeout(() => {
        gridRefs.current["grid3"].current.changeColumnConfigs(virtual);
      }, 0);
      // cd_var3 매핑
      sizeToSlotRef.current = sizeToSlot;
    } else {
      return;
    }
  };

  const onCellValueChangedH = async (event: CellValueChangedEvent) => {
    if (!event) return;
    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;

    const selectedDataH = gridRefs.current["grid1"].current.getSelectedRows();
    if (selectedDataH.length > 0) {
      selectedDataH.forEach((row: any) => {
        if (row.cd_cust !== row._previousCdCust) {
          row.cd_cust_dlv = row.cd_cust; // cd_cust_dlv에 값을 넣음
        }
      });
    }

    if (colId === "rt_exch") {
      const rt_exch = Number(rowData.rt_exch ?? 1);
      gridRefs.current["grid2"].current.forEachFilteredNode((node: RowNode) => {
        if (node) {
          node.setDataValue("rt_exch", rt_exch, "noEvent");
        }
      });
      await CalAmBulk(
        gridRefs.current["grid1"],
        gridRefs.current["grid2"],
        colId
      );
    } else if (colId === "no_io") {
      // 필터링
      gridRefs.current["grid2"].current.applyFilters({
        no_io: rowData.no_io,
      });
      gridRefs.current["grid3"].current.applyFilters({
        no_io: rowData.no_io,
      });
    } else if (colId === "cd_wh") {
      gridRefs.current["grid2"].current.forEachFilteredNode((node: RowNode) => {
        if (node) {
          node.setDataValue("cd_wh", rowData.cd_wh, "noEvent");
        }
      });
    }
  };

  const onCellValueChangedL = async (event: any) => {
    if (!event) return;

    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;
    if (colId === "qt") {
      rowNode.setDataValue("qt_w", rowData.qt, "noEvent");
    } else if (colId === "qt_divide") {
      rowNode.setDataValue("qt_divide_w", rowData.qt_divide, "noEvent");
    }
    await CalAm(gridRefs.current["grid1"], gridRefs.current["grid2"], colId);
  };

  const onCellValueChangedD = async (event: any) => {
    if (!event) return;

    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;

    const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
    if (
      colId === "qt_wo1" ||
      colId === "qt_wo2" ||
      colId === "qt_wo3" ||
      colId === "qt_wo4" ||
      colId === "qt_wo5" ||
      colId === "qt_wo6" ||
      colId === "qt_wo7" ||
      colId === "qt_wo8" ||
      colId === "qt_wo9" ||
      colId === "qt_wo10" ||
      colId === "qt_wo11" ||
      colId === "qt_wo12" ||
      colId === "qt_wo13" ||
      colId === "qt_wo14" ||
      colId === "qt_wo15" ||
      colId === "qt_wo16" ||
      colId === "qt_wo17" ||
      colId === "qt_wo18" ||
      colId === "qt_wo19" ||
      colId === "qt_wo20"
    ) {
      let qt = 0;
      for (let i = 1; i <= 20; i++) {
        qt += Number(rowData[`qt_wo${i}`] || 0);
      }
      rowNode.setDataValue("qt", qt);

      const footerData = gridRefs.current["grid1"].current.getFooterData();
      focusedNodeH.setDataValue("qt_wo", footerData.qt);
    }
  };

  const setFooterDataToRow = () => {
    const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
    const footerData = gridRefs.current["grid2"].current.getFooterData();
    if (focusedNodeH) {
      focusedNodeH.setDataValue("am", footerData.am);
      focusedNodeH.setDataValue("am_k", footerData.am_k);
      focusedNodeH.setDataValue("am_vat", footerData.am_vat);
      focusedNodeH.setDataValue("am_supply", footerData.am_supply);
    }
  };

  const onTabChanged = (index: string) => {
    setSelectedTab(index);

    if (focusedRowData) {
      const no_io = focusedRowData.no_io;
      gridRefs.current["grid2"].current.selectAndFocusRow(0);
      gridRefs.current["grid2"].current.applyFilters({ no_io });
      gridRefs.current["grid3"].current.selectAndFocusRow(0);
      gridRefs.current["grid3"].current.applyFilters({ no_io });
    }
  };
  const onRowDataReady = () => {
    const focusKey = focusKeyRef.current;
    if (!focusKey) return;

    const grid = gridRefs.current["grid1"].current;

    const rowCount = grid.getDisplayedRowCount();
    let targetIndex = -1;

    for (let i = 0; i < rowCount; i++) {
      const rowNode = grid.getRowNodeAtIndex(i);
      if (!rowNode?.data) continue;

      if (rowNode.data.no_io === focusKey.no_io) {
        targetIndex = i;
        break;
      }
    }

    if (targetIndex >= 0) {
      grid.selectAndFocusRow(targetIndex);
      grid.scrollToIndex?.(targetIndex);
    } else {
      grid.selectAndFocusRow(0);
    }

    focusKeyRef.current = null;
  };
  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onFocusedRowChanged: onFocusedRowChanged,
          onCellValueChanged: onCellValueChangedH,
          onRowDataReady: onRowDataReady,
        },
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
        events: {
          onCellValueChanged: onCellValueChangedL,
        },
      },
      grid3: {
        columns: columns.grid3,
        dataSource: dataSources.grid3,
        events: {
          onCellValueChanged: onCellValueChangedD,
        },
      },
    }),
    [columns, dataSources]
  );

  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>

      <div className="flex-1 h-full min-h-0 space-y-1.5">
        <SnResizeBar direction={"height"} id="panel1" orgSize="49.5%">
          <SnGrid
            ref={gridRefs.current["grid1"]}
            columnConfig={grids.grid1.columns}
            rowData={grids.grid1.dataSource}
            etcProps={{
              rowSelection: "single",
              title: "출하정보",
            }}
            events={grids.grid1.events}
          />
        </SnResizeBar>
        <SnResizeBar direction={"height"} id="panel2" orgSize="49.5%">
          <SnTab
            titles={["내역정보", "Pivot"]}
            width={"100%"}
            onTabChanged={onTabChanged}
            header={[
              <SnTabHeader
                body={
                  <div className="relative flex space-x-2">
                    <SnButton
                      text={"수주적용"}
                      onClick={() => {
                        onBtnsClick("aButtonApply");
                      }}
                    />
                  </div>
                }
              />,
              <SnTabHeader
                body={
                  <div className="relative flex space-x-2">
                    <SnButton
                      text={"수주적용"}
                      onClick={() => {
                        onBtnsClick("aButtonApply");
                      }}
                    />
                  </div>
                }
              />,
            ]}
            body={[
              <div className="h-full">
                <SnGrid
                  ref={gridRefs.current["grid2"]}
                  columnConfig={grids.grid2.columns}
                  rowData={grids.grid2.dataSource}
                  etcProps={{
                    rowSelection: "multiple",
                    enableInitialSort: false,
                  }}
                  events={grids.grid2.events}
                />
              </div>,
              <div className="h-full">
                <SnGrid
                  ref={gridRefs.current["grid3"]}
                  columnConfig={grids.grid3.columns}
                  rowData={grids.grid3.dataSource}
                  etcProps={{
                    rowSelection: "multiple",
                    enableInitialSort: false,
                  }}
                  events={grids.grid3.events}
                />
              </div>,
            ]}
          />
        </SnResizeBar>
        <SnFileUploadBox
          selectedDocu={selectedDocu}
          selectedLineDocu={selectedLineDocu}
        />
      </div>
    </div>
  );
});

export default SN_MTL_INV_REG_001_TEX;
